/* eslint-disable @typescript-eslint/naming-convention */
import { CancelToken } from 'axios';
import { RouteComponentProps } from 'react-router';
import { SortOrder } from '../../shared/enums/order';
import { ProspectsFilters } from '../../shared/types/prospects-filters';
import {
  ContactStatusType,
  SortOrder as TableSortOrder,
} from '../sequence/enums';
import type {
  FilterLogic,
  FilterOperation,
  FilterSource,
} from './components/prospect-filters-modal/types';
import {
  AiColumnsActionType,
  SupportedAiModelsOutput,
  SupportedAiModelsOutputColumnType,
} from './components/ai-col-enrichment-modal/enums';
import {
  ProspectSortBy as ContactFilterSortBy,
  EnrichmentTemplateCategory,
  ProspectNotePinUnpinActionType,
  SupportedAiModelsServiceProvider,
} from './enums/prospect';
import { AttachmentType } from '../../shared/editor/types';

export type IProps = RouteComponentProps;

export type ProspectParams = {
  email?: string;
  search?: string;
  sequenceId?: number;
  pageSize: number;
  pageNum: number;
  tags?: string[];
  status?: ContactStatusType[];
  sortBy?: ProspectSortBy;
  sortOrder?: TableSortOrder;
  isSequenceNone?: boolean;
};

export type ProspectFilters = {
  sortBy: ProspectSortBy;
  sortOrder: TableSortOrder;
};

export type ProspectCountParams = {
  email?: string;
  search?: string;
  sequenceId?: number;
};

export type ProspectPagination = {
  pageNum: number;
  pageSize: number;
};

export type AddToStepParams = {
  stepId?: number;
  sequenceId: string;
  contactIds: number[];
};

export type removeProspectParam = {
  contactId: number;
  sequenceIds: number[];
};

export type markAsRepliedParam = {
  contactId: number;
  stepId: number;
};

export type removeFromSequenceParam = {
  contactIds: number[];
  sequenceId: number;
};

export type SaveContactAttributesPayload = {
  contactId: number;
  attributes: [];
};

export type saveContactDealValuePayload = {
  sequenceId: number;
  attributes: [];
};

export type AddProspectManuallyPayload = {
  attributes: any[];
  callId?: number;
};

export enum TagsGenerationType {
  All = 'all',
  System = 'System',
  User = 'User',
}

export type AddTagsToProspectsPayload = {
  ids: number[];
  prospects: number[];
  newTags: string[];
};

export type RemoveTagsFromProspectsPayload = {
  tagId: number;
  prospects: number[];
}[];

type BulkActionFilters = ProspectsFilters & {
  sortBy?: ProspectSortBy;
  sortOrder?: TableSortOrder;
  search?: string;
  isSequenceNone?: boolean;
};

export type BulkActionPayload = {
  deSelectedContactIds?: number[];
  contactsFilter?: FilterGroupLogicPayload;
  stepId?: number;
  sequenceId?: string;
  ids?: number[];
  newTags?: string[];
  tagIds?: number[];
  search?: string;
  sortOrder?: SortOrder;
  sortBy?: ContactFilterSortBy;
};

export enum ProspectSortBy {
  Owner = 'owner',
}

export type SelectAllContacts = {
  isAllProspectsSelected?: boolean;
  contactsFilter?: BulkActionFilters;
  deSelectedContactIds?: any[];
};

export type deleteSecondaryEmailParams = {
  prospectInfoId: number;
  contactId: number;
};

export type changePrimaryEmailInProspectPayload = {
  primaryEmailProspectInfoId: number;
  contactId: number;
};

export type deleteSecondaryPhoneParams = {
  prospectInfoId: number;
  contactId: number;
};

export type FilterGroupLogic = {
  filters: {
    group: number;
    conditions: {
      source: string;
      field: string;
      operation: string;
      value?: string | number | (string | number)[];
    }[];
    logic: FilterLogic;
  }[];
  group_logic: FilterLogic;
};

// Filters

export type FilterGroupLogicPayload = {
  selectedFields?: string[];
  sorting?: {
    field: string;
    order: SortOrder;
  }[];
  filters: {
    group: number;
    conditions: {
      source: FilterSource;
      field: string;
      operation: FilterOperation | null;
      value?: string | number | (string | number)[];
    }[];
    logic: FilterLogic;
  }[];
  group_logic: FilterLogic;
};

export type ProspectListPayload = {
  filterGroupLogic?: FilterGroupLogicPayload;
  pageSize?: number;
  pageNum?: number;
  sortOrder?: SortOrder;
  sortBy?: ContactFilterSortBy | number;
  search?: string;
  selectedField?: string[];
  viewId?: number;
  viewType?: string;
  kanbanViewId?: number;
  activeColumns?: string[];
};

export type Filters = {
  filterGroupLogic?: FilterGroupLogicPayload;
  pageNum: number;
  pageSize: number;
  selectedFields: any[];
  sortBy: ContactFilterSortBy;
  sortOrder?: SortOrder;
  search: string;

  shouldFetchProspectList: boolean;
  shouldFetchProspectListCount: boolean;
  shouldFetchSavedFilters: boolean;
};

export type ProspectListByIdsPayload = {
  contactIds: string[];
  selectedField: string[];
};

export type SavedFilter = {
  id: number;
  kanbanViewId?: number;
  shAccountId: number;
  filterName: string;
  isShared: boolean;
  pinnedAt: string | null;
  createdAt: string;
  updatedAt: string;
  filterCriteria: FilterGroupLogicPayload;
  created: {
    id: number;
    firstName: string;
    lastName: string;
  };
  teams: any[];
};

export enum PinUnpinActionType {
  Pin = 'pin',
  UnPin = 'unpin',
}

export type ProspectFiltersSequencesClientsPayload = {
  search?: string;
  page: number;
  pageSize: number;
  selectedIds?: (string | number)[];
};

export type ProspectFiltersValuesPayload = {
  search?: string;
  page: number;
  pageSize: number;
  key: string;
  selectedIds?: (string | number)[];
};

export type ProspectFiltersSaveViewPayload = {
  filterName: string;
  filterCriteria: FilterGroupLogicPayload;
  isShared?: boolean;
  teamIds?: number[];
};

export type ProspectFiltersUpdateViewPayload = Partial<ProspectFiltersSaveViewPayload> & {
  id: string;
};

export type ProspectFieldPayload = {
  contactId: number;
  fieldRef: number;
  attributeValue: string;
};

export type AiColumnTemplateListPayload = {
  id: number;
  templateName: string;
  isWebSearched: boolean;
  description: string;
  tags: string[];
  ai: string;
};

export type saveAndDontRunPayload = {
  actionType: AiColumnsActionType;
  templateId?: number;
  columnName?: string;
  fieldId?: any;
  aiModelId: number;
  instructions: string;
  mergeTags: string[];
  selectedOutputFormat: SupportedAiModelsOutput;
  prospectIds?: number[];
  customOutputFormatPrompt?: string;
  customOutputFormatColumnType?: SupportedAiModelsOutputColumnType;
  isWebSearchEnabled?: boolean;
  unSelectedProspectIds?: string[];

  aiColumnEnrichmentFilters?: {
    filterGroup: FilterGroupLogicPayload;
    sortBy: string;
    sortOrder: SortOrder;
    search: string;
  };
};

export type createAiColumnTemplatePayload = {
  title: string;
  description?: string;
  isWebSearchEnabled: boolean;
  mergeTags: string[];
  shSupportedAiModel: number;
  instructions: string;
  selectedOutputFormat: string;
  customOutputFormatPrompt?: string;
  customOutputFormatColumnType?: string;
};

export type deleteAiColumnTemplatePayload = {
  templateId: number;
};

export interface EnrichmentTemplateList {
  id: number;
  name: string;
  description: string;
  tools: string[];
  serviceProvider: SupportedAiModelsServiceProvider;
  mergeTags: string[];
  creditUsagePerRow: number;
  category: EnrichmentTemplateCategory;
  status: string;
  createdByUser?: {
    id: number;
    firstName: string;
    lastName: string;
  };
}

export interface getAiModelsProps {
  id: number;
  model: string;
  description: string | null;
  serviceProvider: SupportedAiModelsServiceProvider;
  toolCalls: string[];
  outputFormat: SupportedAiModelsOutput[];
  isRecommended: boolean;
  creditUsagePerRow: number;
  status: '0' | '1';
  analysisIdentifier: string; // base64 token containing price & markup percentage
}
export interface getTemplateByIdParams {
  templateId: number | string;
  cancelToken?: CancelToken;
}
export interface getTemplateByIdProps {
  id: number;
  columnName: string;
  shareableId: string;
  name: string;
  description: string;
  userInstructions: string;
  category: EnrichmentTemplateCategory;
  toolCalls: string[];
  suggestedAiModel: {
    id: number;
    model: string;
    serviceProvider: SupportedAiModelsServiceProvider;
    tools: string[];
    creditUsagePerRow: number;
    outputFormat: [];
    columnType: string[];
  };
  selectedOutputFormat: SupportedAiModelsOutput;
  customOutputFormatPrompt: null;
  customOutputFormatColumnType: null;
  isAutoRunEnabled: boolean;
  autoRunConditions: any;
}

export interface getAiColumnPreviewProps {
  aiModelId: number;
  instructions: string;
  prospectId: number;
  selectedOutputFormat: SupportedAiModelsOutput;
  customOutputFormatPrompt?: string;
}

export interface getTemplateConfigProps {
  id: number;
  instructions: string;
  shSupportedAiModelId: number;
  toolCalls: string[];
  selectedOutputFormat: SupportedAiModelsOutput;
  customOutputFormatPrompt: string;
  customOutputFormatColumnType: SupportedAiModelsOutputColumnType;
  templateId?: number;
  mergeTags?: string[];
  isAutoRunEnabled: boolean;
  autoRunConditions: any;
}

export interface saveRegeneratedTemplateProps {
  fieldId: number;
  prospectId: number;
  instructions: string;
  shSupportedAiModelId: number;
  selectedOutputFormat: SupportedAiModelsOutput;
  customOutputFormatPrompt: string;
  customOutputFormatColumnType: SupportedAiModelsOutputColumnType;
}

export interface aiColPlayPauseEnrichmentProps {
  fieldId: number;
}

export interface aiColActiveFieldsStatusProps {
  fieldIds: number[];
}

export interface getEligibilityCheckProps {
  filterGroup: any;
  prospectIds: number[];
}

export type CategoryItem = {
  color: string;
  value: string;
  status: number;
  sortOrder: number;
};

export type KanbanProspectListPayload = {
  id: number;
  name: string;
  categoryFieldId: number | null;
  group?: string;
  isDefault: number;
  shAccountId: number;
  userId: number;
  metadata: {
    categories: CategoryItem[];
    entityType?: string;
  };
  createdAt: string;
};

export enum KanbanUpdateActionType {
  ChangeColor = 'changeColor',
  RenameColumn = 'renameColumn',
  ChangeColumnOrder = 'changeColumnOrder',
  HideUnhideColumn = 'hideUnhideColumn',
}

export type ColumnRename = {
  oldValue: string;
  newValue: string;
};

export type UpdateKanbanViewPayload = {
  categoryFieldId?: number;
  isDefault?: number;
  columnRenames?: ColumnRename[];
  newFieldLabel?: string;
  metadata: {
    entityType?: string;
    categories: CategoryItem[];
  };
};

export type UpdateCustomFieldRequestPayload = {
  label: string;
  fieldType: string; // 'dropdown'
  prospectType: string; // 'contact'
  fallbackText: string;
  metadata: {
    currencyCode?: string;
    options?: string[];
  };
  optionValueMap?: Record<string, string>;
  migrateToOption?: string;
  colorOptions?: string[];
  customFieldId?: number; // Optional as it might be in params, but user put it in body in example
};

export enum visibility {
  Public = 'public',
  Private = 'private',
}

export type GetProspectNotesList = {
  notes: NoteItem[];
  draftNote: NoteItem | null;
  pagination: Pagination;
};

export type Pagination = {
  skip: number;
  take: number;
  hasMore: boolean;
};

export type NoteItem = {
  id: number;
  prospectId: number;
  taskId: number | null;
  userId: number;
  visibility: visibility;
  status: number;
  pinnedAt: string | null;
  createdAt: string;
  updatedAt: string;
  editedAt: string | null;
  editedBy: number | null;

  user: User;
  attachments: Attachment[];
  editor: User | null;
  task: Task | null;
  sequence: any | null;

  content?: string;
  contentUrl?: string | null;
  contentUrlExpiresAt: string | null;
};

export type User = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
};

export type Attachment = {
  id: number;
  name: string;
  mimeType: string;
  size: string;
  preSignedUrl: string;
  expiresAt: string;
};

export type Task = {
  id: number;
  type: string;
  executionType: string;
  metadata: any | null;
  hashId: string;
  title: string;
};

export type CreateProspectNoteProp = {
  prospectId?: number;
  content: string;
  visibility?: visibility;
  taskId?: number;
  attachmentIds?: number[];
  status?: number;
};
export type ProspectNotePin = {
  action: ProspectNotePinUnpinActionType;
};

export type UpdateProspectNoteProps = CreateProspectNoteProp & {
  noteId: number;
};

export type UpdateProspectNoteVisibilityProps = {
  visibility: visibility;
};

export type getProspectNotesParams = {
  taskId?: number;
  skip?: number;
  take?: number;
  sendDraft?: boolean;
  selectedIds?: number[];
  search?: string;
};

export type UpdateProspectPinStatusProps = {
  action: PinUnpinActionType;
  fieldKey: string;
};
export type getSingleProspectPinnedFields = {
  userId: number;
  fieldKey: string;
  createdAt: string;
};

export type getMinimalEmailAccountListProps = {
  skip?: number;
  take?: number;
  search?: string;
  selectedIds?: number[];
};
export type EmailAccount = {
  id: number;
  fromName: string;
  fromEmail: string;
  type: number; // If type has meaning, I can convert this to enum
  status: number; // Same here — can convert to enum if needed
  emailServiceProvider: string;
};

// API response structure
export type EmailAccountMinimalListResponse = {
  emailAccounts: EmailAccount[];
  hasMore: boolean;
};
